import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

/* ============= VOLUME SUB-CALCULATORS ============= */
const vol = (title, subtitle, inputs, formula, formulaLabel) => (props) => {
  const [state, setState] = useState(inputs.reduce((a,i)=>({...a, [i.key]: i.default}), {}));
  const V = formula(state);
  return (
    <CalcShell look="cnet" title={title} subtitle={subtitle}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          {inputs.map(i => (
            <div key={i.key} className="field">
              <label>{i.label}</label>
              <div className="flex gap-2">
                <input type="number" step="0.001" value={state[i.key]} onChange={e=>setState({...state, [i.key]: +e.target.value||0})} data-testid={`v-${i.key}`}/>
                <span className="self-center text-slate-500 text-sm">{i.unit || 'units'}</span>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Volume</div><div className="result-value" data-testid="v-result">{isFinite(V) ? V.toFixed(4) : 'Invalid'} units³</div></div>
          <p className="mt-3 text-sm text-slate-600 font-mono">{formulaLabel}</p>
        </div>
      </div>
      <StepsPanel steps={[formulaLabel, `V ≈ <b>${isFinite(V) ? V.toFixed(4) : 'Invalid'}</b> units³`]}/>
    </CalcShell>
  );
};

export const CubeVolume = vol('Cube Volume Calculator', 'Compute the volume of a cube.',
  [{key:'a', label:'Edge length', default:5}], s => Math.pow(s.a, 3), 'V = a³');
export const SphereVolume = vol('Sphere Volume Calculator', 'Compute the volume of a sphere.',
  [{key:'r', label:'Radius', default:5}], s => (4/3) * Math.PI * Math.pow(s.r, 3), 'V = (4/3)π r³');
export const ConeVolume = vol('Cone Volume Calculator', 'Compute the volume of a right circular cone.',
  [{key:'r', label:'Base radius', default:5}, {key:'h', label:'Height', default:10}], s => (1/3) * Math.PI * s.r*s.r * s.h, 'V = (1/3)π r² h');
export const CylinderVolume = vol('Cylinder Volume Calculator', 'Compute the volume of a cylinder.',
  [{key:'r', label:'Radius', default:5}, {key:'h', label:'Height', default:10}], s => Math.PI * s.r*s.r * s.h, 'V = π r² h');
export const EllipsoidVolume = vol('Ellipsoid Volume Calculator', 'Compute the volume of an ellipsoid.',
  [{key:'a', label:'Semi-axis a', default:3}, {key:'b', label:'Semi-axis b', default:4}, {key:'c', label:'Semi-axis c', default:5}], s => (4/3) * Math.PI * s.a * s.b * s.c, 'V = (4/3)π a·b·c');
export const CapsuleVolume = vol('Capsule Volume Calculator', 'Cylinder with two hemispherical caps.',
  [{key:'r', label:'Radius', default:3}, {key:'h', label:'Cylindrical height', default:8}], s => Math.PI * s.r*s.r * (s.h + (4/3)*s.r), 'V = π r² (h + 4r/3)');
export const RectTankVolume = vol('Rectangular Tank Volume Calculator', 'Volume of a rectangular box/tank.',
  [{key:'l', label:'Length', default:5}, {key:'w', label:'Width', default:4}, {key:'h', label:'Height', default:3}], s => s.l*s.w*s.h, 'V = l·w·h');
export const SphericalCapVolume = vol('Spherical Cap Volume Calculator', 'Volume of a spherical cap.',
  [{key:'h', label:'Cap height', default:3}, {key:'r', label:'Sphere radius', default:6}], s => (Math.PI * s.h*s.h * (3*s.r - s.h))/3, 'V = πh²(3R − h)/3');
export const ConicalFrustumVolume = vol('Conical Frustum Volume Calculator', 'A cone with the top cut off.',
  [{key:'r1', label:'Bottom radius', default:5}, {key:'r2', label:'Top radius', default:3}, {key:'h', label:'Height', default:8}], s => (1/3) * Math.PI * s.h * (s.r1*s.r1 + s.r1*s.r2 + s.r2*s.r2), 'V = (πh/3)(r₁²+r₁r₂+r₂²)');
export const SquarePyramidVolume = vol('Square Pyramid Volume Calculator', 'Volume of a right pyramid with a square base.',
  [{key:'a', label:'Base edge', default:5}, {key:'h', label:'Height', default:10}], s => (s.a*s.a*s.h)/3, 'V = a²h/3');
export const TubeVolume = vol('Tube (Hollow Cylinder) Volume Calculator', 'Volume of a hollow cylindrical tube.',
  [{key:'ro', label:'Outer radius', default:5}, {key:'ri', label:'Inner radius', default:3}, {key:'h', label:'Height', default:10}], s => Math.PI * (s.ro*s.ro - s.ri*s.ri) * s.h, 'V = π(R² − r²)h');

/* ============= SLOPE SUB-CALCULATORS ============= */
export function SlopeTwoPoints() {
  const [x1, setX1] = useState(1); const [y1, setY1] = useState(2);
  const [x2, setX2] = useState(4); const [y2, setY2] = useState(8);
  const dx = x2 - x1, dy = y2 - y1;
  const slope = dx !== 0 ? dy/dx : Infinity;
  const b = y1 - slope * x1;
  const angle = Math.atan(slope) * 180 / Math.PI;
  return (
    <CalcShell title="Slope Calculator — 2 Points" subtitle="Slope, intercept, angle, and distance between two points.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Point 1 (x₁, y₁)</label><div className="grid grid-cols-2 gap-2"><input type="number" value={x1} onChange={e=>setX1(+e.target.value||0)} data-testid="sp-x1"/><input type="number" value={y1} onChange={e=>setY1(+e.target.value||0)} data-testid="sp-y1"/></div></div>
          <div className="field"><label>Point 2 (x₂, y₂)</label><div className="grid grid-cols-2 gap-2"><input type="number" value={x2} onChange={e=>setX2(+e.target.value||0)} data-testid="sp-x2"/><input type="number" value={y2} onChange={e=>setY2(+e.target.value||0)} data-testid="sp-y2"/></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Slope (m)</div><div className="result-value" data-testid="sp-slope">{isFinite(slope) ? slope.toFixed(4) : 'Undefined (vertical)'}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Rise (Δy)</td><td className="text-right">{dy}</td></tr>
            <tr><td>Run (Δx)</td><td className="text-right">{dx}</td></tr>
            <tr><td>y-intercept</td><td className="text-right">{isFinite(b) ? b.toFixed(4) : '—'}</td></tr>
            <tr><td>Angle from x-axis</td><td className="text-right">{isFinite(angle) ? angle.toFixed(3) + '°' : '90°'}</td></tr>
            <tr><td>Line equation</td><td className="text-right font-mono">y = {isFinite(slope) ? slope.toFixed(3) : '∞'}x {isFinite(b) && b >= 0 ? '+ ' : '− '}{Math.abs(b).toFixed(3)}</td></tr>
            <tr><td>Distance</td><td className="text-right">{Math.hypot(dx, dy).toFixed(4)}</td></tr>
          </tbody></table>
        </div>
      </div>
    </CalcShell>
  );
}
export function SlopePointSlope() {
  const [x1, setX1] = useState(1); const [y1, setY1] = useState(2); const [m, setM] = useState(2);
  const b = y1 - m * x1;
  return (
    <CalcShell title="Slope Calculator — Point + Slope" subtitle="Given one point and the slope, find the full line equation.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Point (x₁, y₁)</label><div className="grid grid-cols-2 gap-2"><input type="number" value={x1} onChange={e=>setX1(+e.target.value||0)} data-testid="sps-x"/><input type="number" value={y1} onChange={e=>setY1(+e.target.value||0)} data-testid="sps-y"/></div></div>
          <div className="field"><label>Slope (m)</label><input type="number" step="0.01" value={m} onChange={e=>setM(+e.target.value||0)} data-testid="sps-m"/></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Line Equation</div><div className="result-value font-mono" data-testid="sps-eq">y = {m}x {b >= 0 ? '+ ' : '− '}{Math.abs(b).toFixed(3)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Point-slope form</td><td className="text-right font-mono">y − {y1} = {m}(x − {x1})</td></tr>
            <tr><td>y-intercept (b)</td><td className="text-right">{b.toFixed(4)}</td></tr>
          </tbody></table>
        </div>
      </div>
    </CalcShell>
  );
}

/* ============= DISTANCE SUB-CALCULATORS ============= */
export function Distance2D() {
  const [x1, setX1] = useState(0); const [y1, setY1] = useState(0);
  const [x2, setX2] = useState(3); const [y2, setY2] = useState(4);
  const d = Math.hypot(x2-x1, y2-y1);
  return (
    <CalcShell title="2D Distance Calculator" subtitle="Euclidean distance between two 2D points.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Point 1 (x₁, y₁)</label><div className="grid grid-cols-2 gap-2"><input type="number" value={x1} onChange={e=>setX1(+e.target.value||0)} data-testid="d2-x1"/><input type="number" value={y1} onChange={e=>setY1(+e.target.value||0)} data-testid="d2-y1"/></div></div>
          <div className="field"><label>Point 2 (x₂, y₂)</label><div className="grid grid-cols-2 gap-2"><input type="number" value={x2} onChange={e=>setX2(+e.target.value||0)} data-testid="d2-x2"/><input type="number" value={y2} onChange={e=>setY2(+e.target.value||0)} data-testid="d2-y2"/></div></div>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Distance</div><div className="result-value" data-testid="d2-result">{d.toFixed(6)}</div></div>
          <p className="mt-3 text-sm font-mono text-slate-700">d = √((x₂−x₁)² + (y₂−y₁)²)</p>
        </div>
      </div>
    </CalcShell>
  );
}
export function Distance3D() {
  const [p, setP] = useState({x1:0, y1:0, z1:0, x2:1, y2:2, z2:3});
  const d = Math.hypot(p.x2-p.x1, p.y2-p.y1, p.z2-p.z1);
  const F = (k, l) => <div><label className="text-xs">{l}</label><input type="number" value={p[k]} onChange={e=>setP({...p, [k]: +e.target.value||0})} data-testid={`d3-${k}`}/></div>;
  return (
    <CalcShell title="3D Distance Calculator" subtitle="Euclidean distance between two 3D points.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div><label className="font-semibold">Point 1</label><div className="grid grid-cols-3 gap-2">{F('x1','x₁')}{F('y1','y₁')}{F('z1','z₁')}</div></div>
          <div><label className="font-semibold">Point 2</label><div className="grid grid-cols-3 gap-2">{F('x2','x₂')}{F('y2','y₂')}{F('z2','z₂')}</div></div>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Distance</div><div className="result-value" data-testid="d3-result">{d.toFixed(6)}</div></div>
          <p className="mt-3 text-sm font-mono text-slate-700">d = √((x₂−x₁)² + (y₂−y₁)² + (z₂−z₁)²)</p>
        </div>
      </div>
    </CalcShell>
  );
}
export function DistanceLatLon() {
  const [lat1, setLat1] = useState(40.7128);
  const [lon1, setLon1] = useState(-74.0060);
  const [lat2, setLat2] = useState(51.5074);
  const [lon2, setLon2] = useState(-0.1278);
  // Haversine formula, R in km
  const R = 6371;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLon/2)**2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const km = R * c;
  return (
    <CalcShell title="Distance by Latitude/Longitude Calculator" subtitle="Great-circle distance between two points (Haversine formula).">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Point 1 (lat, lon)</label><div className="grid grid-cols-2 gap-2"><input type="number" step="any" value={lat1} onChange={e=>setLat1(+e.target.value||0)} data-testid="dll-lat1"/><input type="number" step="any" value={lon1} onChange={e=>setLon1(+e.target.value||0)} data-testid="dll-lon1"/></div></div>
          <div className="field"><label>Point 2 (lat, lon)</label><div className="grid grid-cols-2 gap-2"><input type="number" step="any" value={lat2} onChange={e=>setLat2(+e.target.value||0)} data-testid="dll-lat2"/><input type="number" step="any" value={lon2} onChange={e=>setLon2(+e.target.value||0)} data-testid="dll-lon2"/></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Great-Circle Distance</div><div className="result-value" data-testid="dll-km">{km.toFixed(3)} km</div></div>
          <table className="data-table"><tbody>
            <tr><td>Distance in miles</td><td className="text-right">{(km * 0.621371).toFixed(3)} mi</td></tr>
            <tr><td>Distance in nautical miles</td><td className="text-right">{(km / 1.852).toFixed(3)} nmi</td></tr>
          </tbody></table>
        </div>
      </div>
      <StepsPanel steps={[`Haversine: a = sin²(Δφ/2) + cos(φ₁)cos(φ₂)sin²(Δλ/2)`,`c = 2·atan2(√a, √(1−a)); d = R·c with R = 6371 km`, `d ≈ <b>${km.toFixed(3)} km</b>`]}/>
    </CalcShell>
  );
}

/* ============= AREA SUB-CALCULATORS ============= */
export function AreaSector() {
  const [r, setR] = useState(10);
  const [theta, setTheta] = useState(60); // degrees
  const rad = (+theta || 0) * Math.PI / 180;
  const area = 0.5 * (+r || 0) * (+r || 0) * rad;
  const arc = (+r || 0) * rad;
  return (
    <CalcShell title="Sector Area Calculator" subtitle="Area of a circular sector given radius and central angle.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Radius</label><input type="number" step="0.01" value={r} onChange={e=>setR(+e.target.value||0)} data-testid="as-r"/></div>
          <div className="field"><label>Central angle</label><div className="flex gap-2"><input type="number" step="0.01" value={theta} onChange={e=>setTheta(+e.target.value||0)} data-testid="as-theta" className="flex-1"/><span className="self-center text-slate-500">degrees</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Sector Area</div><div className="result-value" data-testid="as-result">{area.toFixed(4)} units²</div></div>
          <table className="data-table"><tbody>
            <tr><td>Angle in radians</td><td className="text-right">{rad.toFixed(4)}</td></tr>
            <tr><td>Arc length</td><td className="text-right">{arc.toFixed(4)}</td></tr>
          </tbody></table>
        </div>
      </div>
      <StepsPanel steps={[`A = ½ r² θ (θ in radians)`, `= 0.5 × ${r}² × ${rad.toFixed(4)} = <b>${area.toFixed(4)}</b> units²`]}/>
    </CalcShell>
  );
}
export function AreaTriangle() {
  const [b, setB] = useState(10);
  const [h, setH] = useState(6);
  const A = 0.5 * (+b||0) * (+h||0);
  return (
    <CalcShell title="Triangle Area Calculator" subtitle="Area of a triangle given base and height.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Base</label><input type="number" step="0.01" value={b} onChange={e=>setB(+e.target.value||0)} data-testid="at-b"/></div>
          <div className="field"><label>Height</label><input type="number" step="0.01" value={h} onChange={e=>setH(+e.target.value||0)} data-testid="at-h"/></div>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Area</div><div className="result-value" data-testid="at-result">{A.toFixed(4)} units²</div></div>
          <p className="mt-3 text-sm font-mono text-slate-700">A = ½ · b · h</p>
        </div>
      </div>
    </CalcShell>
  );
}

/* ============= RATIO — SCALING ============= */
export function RatioScaling() {
  const [a, setA] = useState(3);
  const [b, setB] = useState(4);
  const [scale, setScale] = useState(2);
  return (
    <CalcShell title="Ratio Scaling Calculator" subtitle="Scale a ratio a:b up or down by a factor.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Original ratio</label><div className="grid grid-cols-2 gap-2"><input type="number" value={a} onChange={e=>setA(+e.target.value||0)} data-testid="rs-a"/><input type="number" value={b} onChange={e=>setB(+e.target.value||0)} data-testid="rs-b"/></div></div>
          <div className="field"><label>Scale factor</label><input type="number" step="0.01" value={scale} onChange={e=>setScale(+e.target.value||1)} data-testid="rs-k"/></div>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">Scaled Ratio</div><div className="result-value" data-testid="rs-result">{(a*scale).toFixed(3)} : {(b*scale).toFixed(3)}</div></div>
        </div>
      </div>
    </CalcShell>
  );
}
